import React, { useState, useEffect, useRef, useCallback } from 'react';
import ErpiaSelect from './ErpiaSelect';
import { confirmErpia } from './ConfirmDialog';
import APIService from '../services/api';

/**
 * Éditeur de CV « comme un document » — le CV est une page continue où tout se
 * clique et se tape, mot par mot, avec l'optimiseur intégré.
 *
 * Une seule source de vérité : les sections structurées de la préparation
 * (cv-prep). La page N'EST PAS du texte libre — chaque zone éditable est
 * rattachée à un chemin de section, et l'enregistrement passe par le PATCH de
 * section existant. C'est ce qui permet au matching de relire le contenu, à la
 * régénération de ne rien écraser, et au DOCX final de garder la mise en forme
 * du modèle d'entreprise. On édite le contenu ; le modèle habille.
 *
 * Enregistrement : automatique, à la sortie de chaque zone (blur), sous-arbre
 * par sous-arbre. L'indicateur dit toujours la vérité : « Enregistrement… »
 * pendant l'appel, « Enregistré » après le 2xx, « Échec » sinon — et une
 * section en échec repart dans la file, jamais perdue en silence.
 *
 * Optimiseur : il PROPOSE, l'humain intègre. Chaque suggestion montre
 * l'avant/après au mot près et ne s'applique que sur clic, par la même route
 * que l'édition — mêmes contrôles, même garde-fou anti-invention côté serveur.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

const clone = (v) => JSON.parse(JSON.stringify(v ?? null));

const btnGhost = {
  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px',
  border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)',
  fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
};
const btnPlein = { ...btnGhost, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)' };

const valueAt = (obj, path) => {
  if (path === '') return obj;
  return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
};
const setAt = (obj, path, value) => {
  const ks = path.split('.');
  let cur = obj;
  for (let i = 0; i < ks.length - 1; i++) { if (cur[ks[i]] == null) cur[ks[i]] = /^\d+$/.test(ks[i + 1]) ? [] : {}; cur = cur[ks[i]]; }
  cur[ks[ks.length - 1]] = value;
};

/**
 * LE GRAS. Une seule mise en forme est possible dans cet éditeur, et c'est
 * volontaire : le CV est habillé par le MODÈLE de l'entreprise (police,
 * tailles, couleurs). Ce qu'on écrit ici est du contenu ; le gras est la
 * seule marque qui dit quelque chose du CONTENU — « ce mot-là compte » —
 * plutôt que de la décoration.
 *
 * Il est rangé dans le texte de la section, en `**gras**` : les sections
 * restent des CHAÎNES (le matching les relit, la régénération ne les écrase
 * pas), et le rendu DOCX transforme ces marques en vrai gras en CLONANT le
 * run du modèle — donc sans changer la police. Un gras visible à l'écran et
 * absent du document livré serait un mensonge de l'écran.
 */
const echapperHtml = (t) => String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const enHtml = (t) => echapperHtml(t).replace(/\*\*([\s\S]+?)\*\*/g, '<strong>$1</strong>');

// DOM → texte marqué. On ne lit PAS innerHTML : selon le navigateur et
// l'origine du collage, le gras arrive en <b>, en <strong> ou en
// <span style="font-weight:700">. C'est le poids RENDU qui fait foi.
const enTexte = (noeud, dansGras = false) => {
  let out = '';
  for (const n of noeud.childNodes) {
    if (n.nodeType === 3) { out += n.nodeValue; continue; }
    if (n.nodeType !== 1) continue;
    if (n.tagName === 'BR') { out += '\n'; continue; }
    const poids = n.style?.fontWeight || (window.getComputedStyle ? window.getComputedStyle(n).fontWeight : '');
    const gras = n.tagName === 'B' || n.tagName === 'STRONG'
      || poids === 'bold' || poids === 'bolder' || Number(poids) >= 600;
    const dedans = enTexte(n, dansGras || gras);
    // Jamais de `**` imbriqués : du gras dans du gras, c'est du gras.
    out += (gras && !dansGras && dedans.trim()) ? `**${dedans}**` : dedans;
  }
  return out;
};

/**
 * Zone de texte éditable en place, non contrôlée.
 *
 * Non contrôlée à dessein : pendant la frappe, React ne re-rend pas, donc le
 * curseur ne saute jamais. La valeur n'est lue qu'au blur, et le remontage
 * (par la clé) n'arrive qu'après un commit ou une modification externe —
 * moments où le curseur a déjà quitté la zone.
 */
function Inline({ path, value, onCommit, placeholder, style, multiline = false, onEnter, gras = true }) {
  const ref = useRef(null);
  const commit = () => {
    if (!ref.current) return;
    const txt = (gras ? enTexte(ref.current) : (ref.current.innerText ?? '')).replace(/ /g, ' ').replace(/\n+$/, '');
    if (txt !== String(value ?? '')) onCommit(txt);
  };
  return (
    <span
      ref={ref}
      data-path={path}
      contentEditable
      suppressContentEditableWarning
      onBlur={commit}
      onKeyDown={e => {
        // Ctrl/⌘+B : le raccourci que tout le monde essaie en premier. Sans
        // lui, le navigateur l'applique QUAND MÊME dans un contentEditable —
        // autant que ce soit nous, pour que le commit sache le relire.
        if (gras && (e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'B')) {
          e.preventDefault();
          document.execCommand('bold');
          return;
        }
        if (e.key === 'Enter' && !multiline) {
          e.preventDefault();
          if (onEnter) { commit(); onEnter(); } else ref.current?.blur();
        }
        if (e.key === 'Escape') ref.current?.blur();
      }}
      onPaste={e => {
        // Collage en texte brut : la mise en forme vient du modèle, jamais du
        // presse-papier — un CV collé depuis Word n'importe pas ses styles.
        e.preventDefault();
        document.execCommand('insertText', false, (e.clipboardData || window.clipboardData).getData('text/plain'));
      }}
      style={{
        outline: 'none', minWidth: '20px', display: multiline ? 'block' : 'inline',
        whiteSpace: multiline ? 'pre-wrap' : 'normal', cursor: 'text',
        borderRadius: '3px', ...style,
      }}
      className="cvp-inline"
      data-placeholder={placeholder || ''}
      // Le contenu n'est posé qu'au montage (zone non contrôlée) : la clé de
      // la zone la remonte après un commit ou une modification externe.
      {...(gras
        ? { dangerouslySetInnerHTML: { __html: enHtml(value) } }
        : { children: String(value ?? '') })}
    />
  );
}

/** Rangée de « puces » éditables (compétences, technologies). */
function Chips({ chips, onChange, ajouterLabel }) {
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center' }}>
      {chips.map((c, i) => (
        <span key={`${i}|${c}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px', background: 'var(--chip-bg, #f3f4f6)', fontSize: '12px' }}>
          {/* Pas de gras sur une puce : une compétence est une étiquette, pas
              une phrase où un mot compte plus qu'un autre. */}
          <Inline path={`chip`} value={c} gras={false}
            onCommit={txt => { const n = [...chips]; if (txt.trim()) n[i] = txt.trim(); else n.splice(i, 1); onChange(n); }} />
          <button onClick={() => { const n = [...chips]; n.splice(i, 1); onChange(n); }} title="Retirer"
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-faint)', padding: 0, fontSize: '11px', lineHeight: 1 }}>×</button>
        </span>
      ))}
      <button onClick={() => onChange([...chips, ''])} title={ajouterLabel}
        style={{ border: '1px dashed var(--input-border)', background: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: '2px 8px', borderRadius: '6px', fontSize: '11.5px', fontFamily: 'inherit' }}>
        + {ajouterLabel}
      </button>
    </span>
  );
}

/** Met en évidence le mot-clé dans le texte « après » d'une suggestion. */
function Surligne({ texte, motCle }) {
  if (!motCle) return <>{texte}</>;
  const parts = String(texte || '').split(new RegExp(`(${motCle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'i'));
  return <>{parts.map((p, i) => p.toLowerCase() === motCle.toLowerCase()
    ? <mark key={i} style={{ background: 'rgba(45,200,110,0.25)', borderRadius: '3px', padding: '0 2px' }}>{p}</mark>
    : p)}</>;
}

export default function CvEditeur({ prep, prepId, candidatNom, showNotification, onReload, onClose }) {
  const [cv, setCv] = useState(() => clone(prep?.structured_cv) || {});
  const [saveState, setSaveState] = useState('idle');   // idle | saving | saved | error
  const [saveError, setSaveError] = useState(null);
  // undefined = rien reçu ; null = le serveur a annulé le score (à re-scorer) ;
  // nombre = score courant. On n'affiche que ce que le serveur a dit.
  const [score, setScore] = useState(undefined);
  // Le document Word suit l'édition : enregistrer, c'est écraser l'ancienne
  // version. idle = rien modifié encore ; attente/maj = régénération planifiée
  // ou en cours ; a_jour = le fichier reflète la page ; erreur = à réessayer.
  const [docEtat, setDocEtat] = useState('idle');
  const [rescoring, setRescoring] = useState(false);
  // Optimiseur : null = jamais demandé ; 'chargement' ; 'absent' (route non
  // servie) ; 'erreur' ; sinon la liste des suggestions.
  const [panneau, setPanneau] = useState(false);
  const [sugg, setSugg] = useState(null);
  const [suggBusy, setSuggBusy] = useState({});         // id → true pendant l'intégration
  const [flashPath, setFlashPath] = useState(null);     // zone à illuminer après intégration
  // Sélection de texte : { path, texte, x, y } puis proposition serveur.
  const [selMenu, setSelMenu] = useState(null);
  const [selProp, setSelProp] = useState(null);         // { texte, action } | 'chargement' | 'absent'
  const pageRef = useRef(null);
  // Rattachement d'un besoin sur une préparation libre : le contenu ne bouge
  // pas, mais le score et l'optimiseur se calculent contre ce besoin.
  const [rattache, setRattache] = useState(false);
  const [positionsRat, setPositionsRat] = useState(null); // null = pas chargé
  const [besoinRat, setBesoinRat] = useState('');
  const [ratBusy, setRatBusy] = useState(false);
  const [ratInfo, setRatInfo] = useState(null);

  const dirty = useRef(new Set());
  const validatedWarned = useRef(false);
  const validated = prep?.status === 'VALIDATED_BY_CANDIDATE' || prep?.status === 'READY_FOR_SUBMISSION';
  // Préparation « libre » (aucun besoin rattaché) : score, re-scoring et
  // optimisation ciblée n'ont pas de sens — on ne les montre pas, plutôt que
  // de laisser des boutons qui ne peuvent qu'échouer.
  const sansBesoin = (prep?.besoin_id ?? prep?.besoinId) == null;
  const jsonH = () => ({ Authorization: `Bearer ${APIService.getToken()}`, 'Content-Type': 'application/json' });

  const cvRef = useRef(cv);
  useEffect(() => { cvRef.current = cv; }, [cv]);

  // ── Régénération automatique du document Word ─────────────────────────────
  // Il n'y a pas d'étape « Générer » : comme dans Word, enregistrer suffit.
  // Chaque salve d'enregistrements replanifie une régénération silencieuse qui
  // REMPLACE l'ancienne version du fichier ; une seule à la fois, et si des
  // modifications arrivent pendant la course, on repart aussitôt après.
  const genTimer = useRef(null);
  const genEnCours = useRef(false);
  const genRedemande = useRef(false);

  const genererDoc = async () => {
    if (genEnCours.current) { genRedemande.current = true; return; }
    genEnCours.current = true;
    setDocEtat('maj');
    try {
      const r = await fetch(`${BASE}/cv-prep/${prepId}/generate-docx`, { method: 'POST', headers: { Authorization: `Bearer ${APIService.getToken()}` } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setDocEtat('a_jour');
      await onReload?.();
    } catch (e) {
      setDocEtat('erreur');
      showNotification?.(`Le document n'a pas pu être mis à jour : ${e.message}`, 'error');
    } finally {
      genEnCours.current = false;
      if (genRedemande.current) { genRedemande.current = false; genererDoc(); }
    }
  };
  const genFnRef = useRef(genererDoc);
  genFnRef.current = genererDoc;

  const planifierDoc = () => {
    setDocEtat('attente');
    if (genTimer.current) clearTimeout(genTimer.current);
    genTimer.current = setTimeout(() => { genTimer.current = null; genFnRef.current(); }, 1500);
  };

  // Fermeture pendant l'attente : on n'annule pas la mise à jour, on la lance
  // tout de suite — l'ancienne version ne doit jamais survivre à une édition.
  useEffect(() => () => {
    if (genTimer.current) { clearTimeout(genTimer.current); genFnRef.current(); }
  }, []);

  /** Envoie les sections en attente, une par une, dans l'ordre. */
  const flush = useCallback(async (cvCourant) => {
    const paths = [...dirty.current];
    if (!paths.length) return;
    if (validated && !validatedWarned.current) {
      validatedWarned.current = true;
      showNotification?.('Ce CV était validé par le candidat : modifié, il retombe en brouillon et devra être re-validé.', 'info');
    }
    dirty.current = new Set();
    setSaveState('saving'); setSaveError(null);
    for (const section of paths) {
      try {
        const r = await fetch(`${BASE}/cv-prep/${prepId}/sections`, {
          method: 'PATCH', headers: jsonH(),
          body: JSON.stringify({ section, value: valueAt(cvCourant, section) }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
        if ('score_after' in j) setScore(j.score_after);
      } catch (e) {
        // La section repart dans la file : rien n'est silencieusement perdu.
        dirty.current.add(section);
        setSaveState('error'); setSaveError(`« ${section} » : ${e.message}`);
        return;
      }
    }
    setSaveState('saved');
    planifierDoc(); // enregistré = le fichier Word se remet à jour tout seul
  }, [prepId, validated]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { if (dirty.current.size) flush(cvRef.current); }, [flush]);

  /** Modification locale + mise en file du sous-arbre, envoi immédiat. */
  const commit = (path, dirtyPath, value) => {
    setCv(prev => { const next = clone(prev); setAt(next, path, value); return next; });
    dirty.current.add(dirtyPath);
    setSaveState('idle');
    // Le blur vient d'avoir lieu : on envoie tout de suite, pas d'attente.
    setTimeout(() => flush(cvRef.current), 0);
  };

  /** Écriture directe d'une section (intégration de suggestion, reformulation). */
  const patchSection = async (section, value) => {
    const r = await fetch(`${BASE}/cv-prep/${prepId}/sections`, {
      method: 'PATCH', headers: jsonH(), body: JSON.stringify({ section, value }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
    if ('score_after' in j) setScore(j.score_after);
    setCv(prev => { const next = clone(prev); setAt(next, section, value); return next; });
    setSaveState('saved');
    planifierDoc();
    setFlashPath(section);
    setTimeout(() => setFlashPath(null), 1800);
  };

  const rescorer = async () => {
    setRescoring(true);
    try {
      const r = await fetch(`${BASE}/cv-prep/${prepId}/rescore`, { method: 'POST', headers: { Authorization: `Bearer ${APIService.getToken()}` } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      if ('score_after' in j) setScore(j.score_after);
      else if (j.score != null) setScore(j.score);
      showNotification?.('Score recalculé.', 'success');
      await onReload?.();
    } catch (e) { showNotification?.(`Re-scoring impossible : ${e.message}`, 'error'); }
    finally { setRescoring(false); }
  };

  // ── Gras sur les mots-clés du poste ────────────────────────────────────────
  // Le serveur calcule l'intersection entre ce que le candidat revendique
  // (compétences, technologies) et ce que le poste demande — SANS IA, et sans
  // ajouter ni retirer un mot. Le front ne devine rien : il demande, et
  // affiche ce que le serveur a fait.
  const [grasEnCours, setGrasEnCours] = useState(false);
  const [grasPose, setGrasPose] = useState(false);
  const motsEnGras = (o) => JSON.stringify(o ?? {}).match(/\*\*/g)?.length > 0;
  useEffect(() => { setGrasPose(motsEnGras(cv)); }, [cv]);

  const grasMotsCles = async (retirer = false) => {
    setGrasEnCours(true);
    try {
      const r = await fetch(`${BASE}/cv-prep/${prepId}/gras-mots-cles`, {
        method: 'POST', headers: jsonH(), body: JSON.stringify({ retirer }) });
      if (r.status === 404 || r.status === 501) {
        showNotification?.("La mise en gras des mots-clés arrive côté serveur — rien n'a été modifié.", 'info');
        return;
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      const d = j.data || j;
      if (d.structured_cv) setCv(clone(d.structured_cv));
      showNotification?.(d.message || 'Mots-clés mis en gras.', 'success');
      await onReload?.();
    } catch (e) { showNotification?.(`Mise en gras impossible : ${e.message}`, 'error'); }
    finally { setGrasEnCours(false); }
  };

  // ── Rattachement d'un besoin (préparation libre) ───────────────────────────
  // Les positionnements du candidat, chargés à l'ouverture du volet. Deux
  // routes, comme sur la fiche : la vue candidat d'abord, l'autre en repli.
  useEffect(() => {
    if (!rattache || positionsRat != null) return;
    const candidateId = prep?.candidate_id ?? prep?.candidateId;
    if (candidateId == null) { setPositionsRat([]); return; }
    let annule = false;
    (async () => {
      const essais = [
        [`${BASE}/candidats/vue/${candidateId}`, (j) => {
          const c = j.data || j.candidat || j;
          return c?.positionnements || c?.besoins || j.positionnements;
        }],
        [`${BASE}/candidates/${candidateId}/besoins`, (j) => j.besoins || j.positionnements || j.data || (Array.isArray(j) ? j : [])],
      ];
      for (const [url, extraire] of essais) {
        try {
          const r = await fetch(url, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
          if (!r.ok) continue;
          const arr = extraire(await r.json());
          if (Array.isArray(arr)) { if (!annule) setPositionsRat(arr); return; }
        } catch (e) { /* on tente la route suivante */ }
      }
      if (!annule) setPositionsRat([]);
    })();
    return () => { annule = true; };
  }, [rattache, positionsRat, prep]);

  const besoinsRatOptions = (positionsRat || [])
    .map(p => ({
      id: p.besoin_id ?? p.besoinId ?? p.id,
      titre: p.besoin_titre ?? p.titre ?? p.poste ?? `Besoin ${p.besoin_id ?? p.id}`,
      client: p.client || p.besoin_client,
    }))
    .filter(o => o.id != null);

  const rattacherBesoin = async () => {
    if (!besoinRat) return;
    setRatBusy(true); setRatInfo(null);
    try {
      const r = await fetch(`${BASE}/cv-prep/${prepId}/besoin`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ besoin_id: Number(besoinRat) || besoinRat }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404 || r.status === 501) { setRatInfo('Le rattachement arrive côté serveur — rien n’a été modifié.'); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setRattache(false);
      setScore(null); // le serveur invalide le score au rattachement : à re-scorer
      showNotification?.('Besoin rattaché — le score est à recalculer.', 'success');
      await onReload?.();
    } catch (e) { showNotification?.(`Rattachement impossible : ${e.message}`, 'error'); }
    finally { setRatBusy(false); }
  };

  // ── Optimiseur ─────────────────────────────────────────────────────────────
  const optimiser = async () => {
    setPanneau(true);
    if (Array.isArray(sugg)) return;   // déjà chargées : on rouvre, sans rappel
    setSugg('chargement');
    try {
      const r = await fetch(`${BASE}/cv-prep/${prepId}/suggestions`, { method: 'POST', headers: jsonH(), body: JSON.stringify({}) });
      if (r.status === 404 || r.status === 501) { setSugg('absent'); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      const liste = (j.suggestions || j.data?.suggestions || []).map((x, i) => ({ ...x, _id: x.id ?? i, etat: 'proposee' }));
      setSugg(liste);
      if (!liste.length) showNotification?.('Rien à proposer : le CV couvre déjà les mots-clés du besoin.', 'success');
    } catch (e) { setSugg('erreur'); showNotification?.(`Optimisation impossible : ${e.message}`, 'error'); }
  };

  const integrer = async (s) => {
    setSuggBusy(b => ({ ...b, [s._id]: true }));
    try {
      await patchSection(s.section, s.apres);
      setSugg(l => l.map(x => x._id === s._id ? { ...x, etat: 'integree' } : x));
    } catch (e) { showNotification?.(`Intégration impossible : ${e.message}`, 'error'); }
    finally { setSuggBusy(b => ({ ...b, [s._id]: false })); }
  };
  const ignorer = (s) => setSugg(l => l.map(x => x._id === s._id ? { ...x, etat: 'ignoree' } : x));
  const toutIntegrer = async () => {
    for (const s of (Array.isArray(sugg) ? sugg : [])) {
      if (s.etat === 'proposee') await integrer(s);   // séquentiel : chaque échec s'affiche
    }
  };

  // ── Sélection → mini-menu ──────────────────────────────────────────────────
  const surSelection = () => {
    const sel = window.getSelection();
    const texte = sel?.toString().trim();
    if (!texte || texte.length < 3) { setSelMenu(null); return; }
    const node = sel.anchorNode?.parentElement?.closest?.('[data-path]');
    const zone = node?.getAttribute('data-path');
    if (!zone || zone === 'chip') { setSelMenu(null); return; }
    const rect = sel.getRangeAt(0).getBoundingClientRect();
    const host = pageRef.current?.getBoundingClientRect();
    if (!host) return;
    setSelProp(null);
    setSelMenu({ path: node.getAttribute('data-dirty') || zone, zone, texte, x: rect.left - host.left + rect.width / 2, y: rect.top - host.top });
  };

  const reformuler = async (action) => {
    if (!selMenu) return;
    setSelProp('chargement');
    try {
      const r = await fetch(`${BASE}/cv-prep/${prepId}/reformuler`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ section: selMenu.zone, texte: selMenu.texte, action }),
      });
      if (r.status === 404 || r.status === 501) { setSelProp('absent'); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setSelProp({ action, texte: j.texte || j.data?.texte || '' });
    } catch (e) { setSelProp(null); setSelMenu(null); showNotification?.(`Reformulation impossible : ${e.message}`, 'error'); }
  };

  const appliquerProposition = async () => {
    if (!selMenu || !selProp || typeof selProp !== 'object') return;
    const actuel = String(valueAt(cvRef.current, selMenu.zone) ?? '');
    if (!actuel.includes(selMenu.texte)) {
      showNotification?.('Le texte a changé entre-temps — resélectionnez la phrase.', 'error');
      setSelMenu(null); setSelProp(null); return;
    }
    try {
      await patchSection(selMenu.zone, actuel.replace(selMenu.texte, selProp.texte));
      setSelMenu(null); setSelProp(null);
    } catch (e) { showNotification?.(`Application impossible : ${e.message}`, 'error'); }
  };

  // ── Données ────────────────────────────────────────────────────────────────
  const exp = Array.isArray(cv.experience) ? cv.experience : [];
  const edu = Array.isArray(cv.education) ? cv.education : [];
  const langues = Array.isArray(cv.languages) ? cv.languages : [];
  const skills = Array.isArray(cv.skills?.technical_skills) ? cv.skills.technical_skills : [];

  const etatTexte = {
    idle: null,
    saving: { txt: 'Enregistrement…', color: 'var(--text-3)', icon: 'arrow-repeat' },
    saved: { txt: 'Enregistré', color: '#16a34a', icon: 'check2' },
    error: { txt: saveError || 'Échec d\'enregistrement', color: 'var(--brand)', icon: 'exclamation-triangle-fill' },
  }[saveState];

  const scoreTexte = score === undefined ? null : score === null ? 'Score : à re-scorer' : `Score : ${Math.round(Number(score))}`;
  const nbAProposer = Array.isArray(sugg) ? sugg.filter(s => s.etat === 'proposee').length : 0;

  const secTitre = { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--brand)', margin: '22px 0 8px', borderBottom: '2px solid var(--brand)', paddingBottom: '4px' };
  const flash = (p) => flashPath && (p === flashPath || p.startsWith(`${flashPath}.`) || flashPath.startsWith(`${p}.`))
    ? { background: 'rgba(45,200,110,0.14)', transition: 'background .4s' } : {};

  return (
    <div style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <style>{`
        .cvp-inline:hover { background: rgba(120,130,150,0.08); }
        .cvp-inline:focus { background: rgba(120,130,150,0.12); box-shadow: 0 0 0 2px rgba(99,102,241,0.35); }
        .cvp-inline:empty:before { content: attr(data-placeholder); color: var(--text-faint); }
        .cvp-ajout { opacity: 0; transition: opacity .15s; }
        .cvp-bloc:hover .cvp-ajout, .cvp-page:hover > .cvp-ajout { opacity: 1; }
        @keyframes cvp-spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* ── Barre d'outils ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '10px 0', position: 'sticky', top: 0, background: 'var(--panel, #fff)', zIndex: 5, borderBottom: '1px solid var(--hair)' }}>
        <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--text-strong)', marginRight: '4px' }}>
          {candidatNom || cv.name || 'CV'}
        </span>
        {etatTexte && (
          <span style={{ fontSize: '11.5px', color: etatTexte.color, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <BI icon={etatTexte.icon} style={saveState === 'saving' ? { animation: 'cvp-spin .8s linear infinite' } : undefined} />
            {etatTexte.txt}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {!sansBesoin && scoreTexte && (
          <span style={{ fontSize: '12px', fontWeight: 600, padding: '4px 10px', borderRadius: '7px', background: score === null ? 'rgba(217,119,6,0.12)' : 'var(--chip-bg)', color: score === null ? '#b45309' : 'var(--text-2)' }}>
            {scoreTexte}
          </span>
        )}
        {!sansBesoin && score === null && (
          <button onClick={rescorer} disabled={rescoring} style={btnGhost}>
            <BI icon={rescoring ? 'arrow-repeat' : 'stars'} style={rescoring ? { animation: 'cvp-spin .8s linear infinite' } : undefined} /> {rescoring ? 'Re-scoring…' : 'Re-scorer'}
          </button>
        )}
        {!sansBesoin && (
          <button onClick={optimiser} style={{ ...(panneau ? btnPlein : btnGhost) }}>
            <BI icon="intersect" /> Optimiser{nbAProposer > 0 ? ` (${nbAProposer})` : ''}
          </button>
        )}
        {sansBesoin && (
          <button onClick={() => { setRattache(v => !v); setRatInfo(null); }} style={rattache ? btnPlein : btnGhost}
            title="Le score et l'optimiseur se calculent contre un besoin — rattachez-en un pour les activer.">
            <BI icon="link-45deg" /> Rattacher un besoin
          </button>
        )}
        {/* Pas de bouton « Générer » : enregistrer écrase l'ancienne version,
            le fichier Word suit la page tout seul. */}
        {docEtat !== 'idle' && (
          <span style={{ fontSize: '12px', fontWeight: 600, padding: '4px 10px', borderRadius: '7px', display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: docEtat === 'erreur' ? 'rgba(226,35,26,0.1)' : docEtat === 'a_jour' ? 'rgba(22,163,74,0.1)' : 'var(--chip-bg)',
            color: docEtat === 'erreur' ? 'var(--brand)' : docEtat === 'a_jour' ? '#16a34a' : 'var(--text-2)' }}>
            {docEtat === 'a_jour' && <><BI icon="file-earmark-word" /> CV à jour</>}
            {(docEtat === 'attente' || docEtat === 'maj') && <><BI icon="arrow-repeat" style={{ animation: 'cvp-spin .8s linear infinite' }} /> Mise à jour du CV…</>}
            {docEtat === 'erreur' && <>
              <BI icon="exclamation-triangle-fill" /> CV non mis à jour
              <button onClick={() => genFnRef.current()} style={{ border: 'none', background: 'none', color: 'inherit', fontFamily: 'inherit', fontSize: '12px', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                Réessayer
              </button>
            </>}
          </span>
        )}
        {/* LES MOTS-CLÉS DU POSTE, EN GRAS, SANS IA. Ce que le candidat
            revendique et que le poste demande : l'intersection existe déjà des
            deux côtés, aucun modèle n'est appelé — et aucun mot n'est ajouté
            ni reformulé, seule la mise en forme change. */}
        {!sansBesoin && (
          <button onClick={() => grasMotsCles(false)} disabled={grasEnCours} style={btnGhost}
            title="Met en gras les mots-clés du poste déjà présents dans ce CV — sans rien ajouter ni reformuler">
            <BI icon={grasEnCours ? 'arrow-repeat' : 'type-bold'} style={grasEnCours ? { animation: 'cvp-spin .8s linear infinite' } : undefined} />
            {grasEnCours ? 'Mise en gras…' : 'Mots-clés en gras'}
          </button>
        )}
        {grasPose && (
          <button onClick={() => grasMotsCles(true)} disabled={grasEnCours} style={btnGhost}
            title="Remettre tout le texte en normal">
            <BI icon="eraser" /> Retirer le gras
          </button>
        )}
        {/* GRAS. Le bouton agit sur ce qui est sélectionné dans la zone en
            cours d'édition : `onMouseDown` + preventDefault empêche le clic de
            retirer la sélection — sans ça, le bouton ne trouverait plus rien à
            mettre en gras au moment où on le presse. */}
        <button onMouseDown={e => { e.preventDefault(); document.execCommand('bold'); }}
          style={{ ...btnGhost, fontWeight: 800, minWidth: '34px', justifyContent: 'center' }}
          title="Mettre en gras la sélection (Ctrl/⌘ + B) — le gras suit jusque dans le CV Word">
          G
        </button>
        {onClose && <button onClick={onClose} style={btnGhost}>Fermer</button>}
      </div>

      {/* ── Volet de rattachement (préparation libre) ── */}
      {sansBesoin && rattache && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap', padding: '11px 13px', marginTop: '12px', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--surface)' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', flex: 'none' }}>Rattacher ce CV à un besoin :</span>
          <ErpiaSelect value={besoinRat} onChange={e => setBesoinRat(e.target.value)} disabled={positionsRat == null}
            style={{ flex: 1, minWidth: '220px', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--panel, #fff)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', outline: 'none' }}>
            <option value="">{positionsRat == null ? 'Chargement…' : '— Choisir un besoin —'}</option>
            {besoinsRatOptions.map(o => <option key={o.id} value={o.id}>{o.titre}{o.client ? ` · ${o.client}` : ''}</option>)}
          </ErpiaSelect>
          <button onClick={rattacherBesoin} disabled={!besoinRat || ratBusy} style={{ ...btnPlein, opacity: (!besoinRat || ratBusy) ? 0.55 : 1 }}>
            <BI icon={ratBusy ? 'arrow-repeat' : 'link-45deg'} style={ratBusy ? { animation: 'cvp-spin .8s linear infinite' } : undefined} />
            {ratBusy ? 'Rattachement…' : 'Rattacher'}
          </button>
          <button onClick={() => setRattache(false)} style={btnGhost}>Annuler</button>
          <span style={{ width: '100%', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>
            {Array.isArray(positionsRat) && positionsRat.length === 0
              ? "Ce candidat n'est positionné sur aucun besoin — positionnez-le d'abord depuis sa fiche, puis revenez ici."
              : "Le contenu du CV ne change pas : seul le score et l'optimiseur se calculeront désormais contre ce besoin."}
          </span>
          {ratInfo && (
            <span style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', background: 'var(--chip-bg)', fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
              {ratInfo}
            </span>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginTop: '14px' }}>

        {/* ── La page ── */}
        <div ref={pageRef} onMouseUp={surSelection}
          className="cvp-page"
          style={{ position: 'relative', flex: 1, minWidth: 0, maxWidth: '820px', margin: '0 auto', background: '#fff', color: '#16181d', border: '1px solid var(--border)', borderRadius: '4px', boxShadow: '0 2px 14px rgba(10,12,16,0.07)', padding: '46px 54px 54px', fontSize: '13.5px', lineHeight: 1.55 }}>

          <div style={{ fontSize: '11px', color: 'var(--text-faint)', textAlign: 'right', marginBottom: '10px', userSelect: 'none' }}>
            Cliquez n'importe où et tapez — la mise en forme finale (couleurs, en-tête) est celle du modèle Word.
          </div>

          <div style={flash('name')}>
            <Inline path="name" value={cv.name} placeholder="Prénom NOM"
              onCommit={v => commit('name', 'name', v)}
              style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '-0.02em', display: 'block' }} />
          </div>
          <div style={flash('title')}>
            <Inline path="title" value={cv.title} placeholder="Titre du poste"
              onCommit={v => commit('title', 'title', v)}
              style={{ fontSize: '14px', color: '#5a6172', display: 'block', marginTop: '2px' }} />
          </div>

          <div style={secTitre}>Profil</div>
          <div style={flash('profile')}>
            <Inline path="profile" value={cv.profile} multiline placeholder="Quelques lignes de présentation…"
              onCommit={v => commit('profile', 'profile', v)} />
          </div>

          <div style={secTitre}>Compétences</div>
          <div style={flash('skills.technical_skills')}>
            <Chips chips={skills} ajouterLabel="compétence"
              onChange={n => commit('skills.technical_skills', 'skills.technical_skills', n)} />
          </div>

          <div style={secTitre}>Expérience</div>
          {exp.map((e, i) => (
            <div key={i} className="cvp-bloc" style={{ marginBottom: '16px', ...flash(`experience.${i}`) }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700 }}>
                  <Inline path={`experience.${i}.position`} value={e.position} placeholder="Intitulé du poste"
                    onCommit={v => commit(`experience.${i}.position`, `experience.${i}`, v)} />
                </span>
                <span style={{ fontSize: '12px', color: '#5a6172', whiteSpace: 'nowrap' }}>
                  <Inline path={`experience.${i}.start_date`} value={e.start_date} placeholder="début"
                    onCommit={v => commit(`experience.${i}.start_date`, `experience.${i}`, v)} />
                  {' – '}
                  <Inline path={`experience.${i}.end_date`} value={e.end_date} placeholder="fin"
                    onCommit={v => commit(`experience.${i}.end_date`, `experience.${i}`, v)} />
                </span>
              </div>
              <div style={{ fontSize: '12.5px', color: '#5a6172' }}>
                <Inline path={`experience.${i}.company`} value={e.company} placeholder="Entreprise"
                  onCommit={v => commit(`experience.${i}.company`, `experience.${i}`, v)} />
                {' · '}
                <Inline path={`experience.${i}.location`} value={e.location} placeholder="Lieu"
                  onCommit={v => commit(`experience.${i}.location`, `experience.${i}`, v)} />
              </div>
              <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                {(Array.isArray(e.tasks) ? e.tasks : []).map((t, j) => (
                  <li key={`${j}|${t}`} style={{ marginBottom: '2px', ...flash(`experience.${i}.tasks.${j}`) }}>
                    <Inline path={`experience.${i}.tasks.${j}`} value={t} placeholder="Tâche…"
                      onCommit={v => {
                        const tasks = [...(e.tasks || [])];
                        if (v.trim()) tasks[j] = v; else tasks.splice(j, 1);   // vidée = retirée
                        commit(`experience.${i}.tasks`, `experience.${i}`, tasks);
                      }}
                      onEnter={() => {
                        const tasks = [...(cvRef.current.experience?.[i]?.tasks || [])];
                        tasks.splice(j + 1, 0, '');
                        commit(`experience.${i}.tasks`, `experience.${i}`, tasks);
                        setTimeout(() => document.querySelector(`[data-path="experience.${i}.tasks.${j + 1}"]`)?.focus(), 60);
                      }} />
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: '6px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="cvp-ajout" onClick={() => {
                  const tasks = [...(e.tasks || []), ''];
                  commit(`experience.${i}.tasks`, `experience.${i}`, tasks);
                  setTimeout(() => document.querySelector(`[data-path="experience.${i}.tasks.${tasks.length - 1}"]`)?.focus(), 60);
                }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: '11.5px', fontFamily: 'inherit', padding: 0 }}>+ tâche</button>
                <Chips chips={Array.isArray(e.technologies) ? e.technologies : []} ajouterLabel="techno"
                  onChange={n => commit(`experience.${i}.technologies`, `experience.${i}`, n)} />
                <span style={{ flex: 1 }} />
                <button className="cvp-ajout" onClick={async () => {
                  if (!await confirmErpia({ message: `Retirer l'expérience « ${e.position || e.company || i + 1} » du CV ?` })) return;
                  const n = [...exp]; n.splice(i, 1); commit('experience', 'experience', n);
                }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--danger-fg, #b42318)', fontSize: '11.5px', fontFamily: 'inherit', padding: 0 }}>retirer</button>
              </div>
            </div>
          ))}
          <button className="cvp-ajout" onClick={() => commit('experience', 'experience', [...exp, { position: '', company: '', location: '', start_date: '', end_date: '', tasks: [''], technologies: [] }])}
            style={{ ...btnGhost, padding: '5px 10px', fontSize: '11.5px' }}>+ Ajouter une expérience</button>

          <div style={secTitre}>Formation</div>
          {edu.map((d, i) => (
            <div key={i} className="cvp-bloc" style={{ marginBottom: '6px', ...flash('education') }}>
              <span style={{ fontWeight: 600 }}>
                <Inline path={`education.${i}.degree`} value={d.degree} placeholder="Diplôme"
                  onCommit={v => commit(`education.${i}.degree`, 'education', v)} />
              </span>
              {' — '}
              <Inline path={`education.${i}.field`} value={d.field} placeholder="Domaine"
                onCommit={v => commit(`education.${i}.field`, 'education', v)} />
              <span style={{ fontSize: '12.5px', color: '#5a6172' }}>
                {' · '}
                <Inline path={`education.${i}.institution`} value={d.institution} placeholder="Établissement"
                  onCommit={v => commit(`education.${i}.institution`, 'education', v)} />
                {' ('}
                <Inline path={`education.${i}.end_date`} value={d.end_date} placeholder="année"
                  onCommit={v => commit(`education.${i}.end_date`, 'education', v)} />
                {')'}
              </span>
              <button className="cvp-ajout" onClick={() => { const n = [...edu]; n.splice(i, 1); commit('education', 'education', n); }}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--danger-fg, #b42318)', fontSize: '11px', fontFamily: 'inherit', marginLeft: '8px' }}>retirer</button>
            </div>
          ))}
          <button className="cvp-ajout" onClick={() => commit('education', 'education', [...edu, { degree: '', field: '', institution: '', end_date: '' }])}
            style={{ ...btnGhost, padding: '5px 10px', fontSize: '11.5px' }}>+ Ajouter une formation</button>

          <div style={secTitre}>Langues</div>
          <div style={flash('languages')}>
            {langues.map((l, i) => (
              <span key={i} style={{ marginRight: '14px' }}>
                <Inline path={`languages.${i}.name`} value={l.name} placeholder="Langue"
                  onCommit={v => commit(`languages.${i}.name`, 'languages', v)} />
                {' — '}
                <span style={{ color: '#5a6172' }}>
                  <Inline path={`languages.${i}.proficiency`} value={l.proficiency} placeholder="niveau"
                    onCommit={v => commit(`languages.${i}.proficiency`, 'languages', v)} />
                </span>
                <button className="cvp-ajout" onClick={() => { const n = [...langues]; n.splice(i, 1); commit('languages', 'languages', n); }}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--danger-fg, #b42318)', fontSize: '11px', marginLeft: '4px' }}>×</button>
              </span>
            ))}
            <button className="cvp-ajout" onClick={() => commit('languages', 'languages', [...langues, { name: '', proficiency: '' }])}
              style={{ ...btnGhost, padding: '3px 9px', fontSize: '11.5px' }}>+ langue</button>
          </div>

          {/* ── Mini-menu de sélection ── */}
          {selMenu && (
            <div style={{ position: 'absolute', left: `${Math.max(10, selMenu.x - 120)}px`, top: `${Math.max(0, selMenu.y - 44)}px`, zIndex: 20, background: 'var(--accent, #16181d)', color: 'var(--accent-fg, #fff)', borderRadius: '9px', boxShadow: '0 8px 24px rgba(10,12,16,0.3)', padding: '5px', display: 'flex', gap: '2px', alignItems: 'center' }}
              onMouseDown={e => e.preventDefault()}>
              {selProp == null && (<>
                {[['reformuler', 'Reformuler'], ['condenser', 'Condenser']].map(([a, l]) => (
                  <button key={a} onClick={() => reformuler(a)}
                    style={{ border: 'none', background: 'none', color: 'inherit', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600, padding: '5px 9px', borderRadius: '6px' }}>{l}</button>
                ))}
                <button onClick={() => setSelMenu(null)} style={{ border: 'none', background: 'none', color: 'inherit', cursor: 'pointer', padding: '5px 7px', opacity: 0.7 }}><BI icon="x" /></button>
              </>)}
              {selProp === 'chargement' && <span style={{ fontSize: '12px', padding: '5px 10px' }}><BI icon="arrow-repeat" style={{ animation: 'cvp-spin .8s linear infinite' }} /> Proposition…</span>}
              {selProp === 'absent' && <span style={{ fontSize: '12px', padding: '5px 10px' }}>La reformulation arrive côté serveur.</span>}
              {selProp != null && typeof selProp === 'object' && (
                <div style={{ maxWidth: '380px', padding: '6px 8px' }}>
                  <div style={{ fontSize: '12.5px', lineHeight: 1.45, marginBottom: '6px' }}>{selProp.texte}</div>
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                    <button onClick={() => { setSelMenu(null); setSelProp(null); }}
                      style={{ border: 'none', background: 'rgba(255,255,255,0.15)', color: 'inherit', cursor: 'pointer', fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 600, padding: '4px 9px', borderRadius: '6px' }}>Annuler</button>
                    <button onClick={appliquerProposition}
                      style={{ border: 'none', background: '#fff', color: '#16181d', cursor: 'pointer', fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 700, padding: '4px 9px', borderRadius: '6px' }}>Appliquer</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Panneau Optimiser ── */}
        {panneau && (
          <div style={{ width: '330px', flex: 'none', position: 'sticky', top: '52px' }}>
            <div style={{ border: '1px solid var(--border)', borderRadius: '11px', background: 'var(--panel, #fff)', padding: '13px 14px', maxHeight: '72vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '9px' }}>
                <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-strong)', flex: 1 }}>Mots-clés du besoin</span>
                {Array.isArray(sugg) && nbAProposer > 1 && (
                  <button onClick={toutIntegrer} style={{ ...btnGhost, padding: '4px 9px', fontSize: '11.5px' }}>Tout intégrer</button>
                )}
                <button onClick={() => setPanneau(false)} style={{ ...btnGhost, width: '26px', height: '26px', padding: 0, justifyContent: 'center' }}><BI icon="x" /></button>
              </div>

              {sugg === 'chargement' && <div style={{ fontSize: '12px', color: 'var(--text-3)', padding: '14px 0', textAlign: 'center' }}><BI icon="arrow-repeat" style={{ animation: 'cvp-spin .8s linear infinite' }} /> Analyse du besoin…</div>}
              {sugg === 'absent' && <div style={{ fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.55, padding: '8px 10px', borderRadius: '8px', background: 'var(--code-bg)' }}>L'optimiseur arrive côté serveur — rien n'a été modifié.</div>}
              {sugg === 'erreur' && <button onClick={() => { setSugg(null); optimiser(); }} style={{ ...btnGhost, width: '100%' }}>Réessayer</button>}
              {Array.isArray(sugg) && sugg.length === 0 && (
                <div style={{ fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.55 }}>
                  <BI icon="check-circle-fill" style={{ color: '#16a34a', marginRight: '5px' }} />
                  Le CV couvre déjà les mots-clés du besoin.
                </div>
              )}
              {Array.isArray(sugg) && sugg.filter(s => s.etat !== 'ignoree').map(s => (
                <div key={s._id} style={{ border: '1px solid var(--hair)', borderRadius: '9px', padding: '9px 11px', marginBottom: '8px', opacity: s.etat === 'integree' ? 0.75 : 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '11.5px', fontWeight: 700, padding: '1px 7px', borderRadius: '5px', background: s.etat === 'integree' ? 'rgba(45,200,110,0.16)' : 'rgba(226,35,26,0.10)', color: s.etat === 'integree' ? '#16a34a' : 'var(--brand)' }}>
                      {s.mot_cle}
                    </span>
                    {s.etat === 'integree' && <span style={{ fontSize: '10.5px', color: '#16a34a', fontWeight: 600 }}><BI icon="check2" /> Intégré</span>}
                  </div>
                  {s.justification && <div style={{ fontSize: '11px', color: 'var(--text-3)', marginBottom: '5px', lineHeight: 1.45 }}>{s.justification}</div>}
                  {s.etat !== 'integree' && (<>
                    {s.avant != null && String(s.avant).trim() !== '' && (
                      <div style={{ fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.45, marginBottom: '3px' }}>
                        <span style={{ fontWeight: 600 }}>Avant :</span> {s.avant}
                      </div>
                    )}
                    <div style={{ fontSize: '11.5px', color: 'var(--text)', lineHeight: 1.45, marginBottom: '7px' }}>
                      <span style={{ fontWeight: 600 }}>Après :</span> <Surligne texte={s.apres} motCle={s.mot_cle} />
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => integrer(s)} disabled={!!suggBusy[s._id]}
                        style={{ ...btnPlein, padding: '5px 11px', fontSize: '11.5px', opacity: suggBusy[s._id] ? 0.6 : 1 }}>
                        <BI icon={suggBusy[s._id] ? 'arrow-repeat' : 'plus-lg'} style={suggBusy[s._id] ? { animation: 'cvp-spin .8s linear infinite' } : undefined} /> Intégrer
                      </button>
                      <button onClick={() => ignorer(s)} style={{ ...btnGhost, padding: '5px 11px', fontSize: '11.5px' }}>Ignorer</button>
                    </div>
                  </>)}
                </div>
              ))}
              {Array.isArray(sugg) && sugg.some(s => s.etat === 'integree') && score === null && (
                <button onClick={rescorer} disabled={rescoring} style={{ ...btnGhost, width: '100%', justifyContent: 'center', marginTop: '4px' }}>
                  <BI icon="arrow-clockwise" /> Re-scorer avec les intégrations
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
